import React from 'react';
import { useIntl } from 'react-intl';
import { Person } from 'types';
import ScrollableUncontrolledPopover from 'views/Widgets/ScrollableUncontrolledPopover';
import {
  CalibrationRecommendation,
  CreateCommentHandler,
  ThreadedCalibrationComment,
} from '../types';
import CalibrationCommentsPopoverContent, {
  ToggleVisibilityHandler,
} from './CalibrationCommentsPopoverContent';

interface Props {
  participant: Person;
  surveyResponseId: number;
  recommendation: CalibrationRecommendation;
  // The count the column already computed for sorting and CSV, passed in
  // rather than recounted here so the number on screen is the same one the
  // table sorted by.
  count: number;
  calibrationComments: ThreadedCalibrationComment[];
  showPrivateComments: boolean;
  onToggleShowPrivate: () => void;
  onPostComment: CreateCommentHandler;
  onToggleVisibility?: ToggleVisibilityHandler;
  currentUserId?: number;
  isViewAdmin?: boolean;
}

// One of the raise/lower/keep counts, showing only that recommendation's own
// comments on hover. The comments column next to it shows every comment at
// once; the point here is to answer "who asked for this, and why" without
// reading through the others.
const RecommendationCountCell: React.FC<Props> = ({
  participant,
  surveyResponseId,
  recommendation,
  count,
  calibrationComments,
  showPrivateComments,
  onToggleShowPrivate,
  onPostComment,
  onToggleVisibility,
  currentUserId,
  isViewAdmin = false,
}) => {
  const { formatMessage, formatNumber } = useIntl();

  const targetId = `calibration-recommendation-${recommendation}-${surveyResponseId}`;

  // Spelled out per recommendation rather than looked up from a map: message
  // extraction only sees descriptors written literally at the call site.
  const countLabel =
    recommendation === 'R'
      ? formatMessage(
          {
            id: 'app.views.widgets.dashboards.calibration_dashboard.participants.column.raise.count',
            defaultMessage:
              '{count, plural, =0 {Nobody recommended raising this rating} one {{count} person recommended raising this rating} other {{count} people recommended raising this rating}}',
          },
          { count }
        )
      : recommendation === 'L'
        ? formatMessage(
            {
              id: 'app.views.widgets.dashboards.calibration_dashboard.participants.column.lower.count',
              defaultMessage:
                '{count, plural, =0 {Nobody recommended lowering this rating} one {{count} person recommended lowering this rating} other {{count} people recommended lowering this rating}}',
            },
            { count }
          )
        : formatMessage(
            {
              id: 'app.views.widgets.dashboards.calibration_dashboard.participants.column.keep.count',
              defaultMessage:
                '{count, plural, =0 {Nobody recommended keeping this rating} one {{count} person recommended keeping this rating} other {{count} people recommended keeping this rating}}',
            },
            { count }
          );

  return (
    <div className="d-flex align-items-center">
      {/* Focusable so the popover is reachable without a pointer; there is no
          click action, so this is a span rather than a button. */}
      <span id={targetId} tabIndex={0} aria-label={countLabel}>
        {formatNumber(count)}
      </span>
      {count > 0 && (
        <ScrollableUncontrolledPopover
          placement="left"
          trigger="hover focus"
          autohide={false}
          target={targetId}
          className={'popover-variable-lg'}
        >
          <CalibrationCommentsPopoverContent
            participant={participant}
            recommendation={recommendation}
            comments={calibrationComments}
            showPrivateComments={showPrivateComments}
            onToggleShowPrivate={onToggleShowPrivate}
            // Replying and the composer are already suppressed in a popover
            // (CommentThread's canReply, and the modal-only composer), so this
            // hover shows the thread plus the visibility toggle - the same
            // controls the comments column's own hover offers.
            canAddComment={false}
            onSubmitComment={onPostComment}
            onToggleVisibility={onToggleVisibility}
            currentUserId={currentUserId}
            isViewAdmin={isViewAdmin}
            variant={'popover'}
            surveyResponseId={surveyResponseId}
          ></CalibrationCommentsPopoverContent>
        </ScrollableUncontrolledPopover>
      )}
    </div>
  );
};

export default React.memo(RecommendationCountCell);
